import { createUniqueId, For, splitProps, Show, type ComponentProps, type JSX } from 'solid-js';
import { Icon } from './Icon';

interface FieldShell {
  label?: string;
  helperText?: string;
  error?: string;
}

function FieldFrame(props: FieldShell & { id: string; children: JSX.Element; class?: string }): JSX.Element {
  return (
    <div class={`tk-field ${props.class ?? ''}`}>
      <Show when={props.label}>
        <label class="tk-label" for={props.id}>
          {props.label}
        </label>
      </Show>
      {props.children}
      <Show when={props.error ?? props.helperText}>
        <span class={`tk-help ${props.error ? 'tk-help--error' : ''}`}>{props.error ?? props.helperText}</span>
      </Show>
    </div>
  );
}

export function Input(props: ComponentProps<'input'> & FieldShell & { mono?: boolean; fieldClass?: string }): JSX.Element {
  const [local, rest] = splitProps(props, ['label', 'helperText', 'error', 'mono', 'fieldClass', 'id', 'class', 'disabled']);
  const id = local.id ?? createUniqueId();
  return (
    <FieldFrame id={id} label={local.label} helperText={local.helperText} error={local.error} class={local.fieldClass}>
      <div class={`tk-control ${local.error ? 'is-invalid' : ''} ${local.disabled ? 'is-disabled' : ''}`}>
        <input
          id={id}
          disabled={local.disabled}
          {...rest}
          class={`tk-input ${local.mono ? 'tk-input--mono' : ''} ${local.class ?? ''}`}
        />
      </div>
    </FieldFrame>
  );
}

export function Textarea(props: ComponentProps<'textarea'> & FieldShell & { fieldClass?: string }): JSX.Element {
  const [local, rest] = splitProps(props, ['label', 'helperText', 'error', 'fieldClass', 'id', 'class']);
  const id = local.id ?? createUniqueId();
  return (
    <FieldFrame id={id} label={local.label} helperText={local.helperText} error={local.error} class={local.fieldClass}>
      <textarea id={id} {...rest} class={`tk-textarea ${local.error ? 'is-invalid' : ''} ${local.class ?? ''}`} />
    </FieldFrame>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export function Select(
  props: ComponentProps<'select'> & FieldShell & { options: SelectOption[]; fieldClass?: string },
): JSX.Element {
  const [local, rest] = splitProps(props, ['label', 'helperText', 'error', 'options', 'fieldClass', 'id', 'class', 'value']);
  const id = local.id ?? createUniqueId();
  return (
    <FieldFrame id={id} label={local.label} helperText={local.helperText} error={local.error} class={local.fieldClass}>
      <div class="tk-select-wrap">
        {/* Selection is driven by `selected` on the options: Solid appends
            children after assigning value, so value alone does not stick. */}
        <select id={id} {...rest} class={`tk-select ${local.class ?? ''}`}>
          <For each={local.options}>
            {(option) => (
              <option value={option.value} selected={option.value === local.value}>
                {option.label}
              </option>
            )}
          </For>
        </select>
        <span class="tk-select-wrap__chevron">
          <Icon name="chevronDown" />
        </span>
      </div>
    </FieldFrame>
  );
}

export function CheckboxCard(props: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  title: string;
  subtitle?: string;
}): JSX.Element {
  return (
    <label class={`tk-checkbox ${props.checked ? 'is-checked' : ''}`}>
      <input
        type="checkbox"
        checked={props.checked}
        onChange={(event) => props.onChange(event.currentTarget.checked)}
      />
      <span class="list-row__main">
        <span class="list-row__title">{props.title}</span>
        <Show when={props.subtitle}>
          <span class="list-row__meta mono">{props.subtitle}</span>
        </Show>
      </span>
    </label>
  );
}
